// The stock page's multi-day chart (Lightweight Charts): any timeframe from 5 minutes to a month over
// ranges from a day to five years, from /v1/candles. Candles and volume only: MoneyHQ's layers belong to
// today's session (ProChart). Times are exchange wall-clock written as UTC, so the axis reads in IST.
import {
  CandlestickSeries, ColorType, CrosshairMode, HistogramSeries, LineSeries, LineStyle, PriceScaleMode, createChart, createSeriesMarkers,
  type IChartApi, type IPriceLine, type ISeriesApi, type ISeriesMarkersPluginApi, type Time, type UTCTimestamp,
} from 'lightweight-charts';
import { PatternLayer, type PatternDraw } from './pattern-layer';
import { TradeLayer, type TradeDraw } from './trade-layer';
import { PlanMarks, type PlanMark } from './marks';
import { palette } from './palette';

export interface Candle { t: number; o: number; h: number; l: number; c: number; v: number }
export type Scale = 'normal' | 'log' | 'pct';
/** MoneyHQ's tested setups drawn on the chart (times match the candles'). */
export interface Overlays {
  /** Breakout entries and exits (the new-high setup). */
  marks: { t: number; kind: 'entry' | 'exit' }[];
  /** The trailing stop while a breakout trade is open; null = no trade (a gap in the line). */
  stop: { t: number; v: number | null }[];
  /** Weekly momentum rank (1 = strongest) and the number of stocks ranked. */
  rank: { t: number; v: number }[];
  rankOf: number;
  /** Classic chart patterns (empty when they are switched off). */
  patterns: PatternDraw[];
}

const alpha = (hex: string, a: number) => `${hex}${Math.round(a * 255).toString(16).padStart(2, '0')}`;

export class HistoryChart {
  readonly chart: IChartApi;
  private candles: ISeriesApi<'Candlestick'>;
  private volume: ISeriesApi<'Histogram'>;
  private data: Candle[] = [];
  private intraday = true;
  /** One line per breakout trade (a single series would bridge the gaps between trades). */
  private stopLines: ISeriesApi<'Line'>[] = [];
  private rank: ISeriesApi<'Line'>;
  private markers: ISeriesMarkersPluginApi<Time>;
  private rankLine: IPriceLine | null = null;
  private overlays: Overlays | null = null;
  private patterns: PatternLayer;
  private planMarks!: PlanMarks;
  private tradeLayer!: TradeLayer;
  private tradeDraw: TradeDraw[] = [];
  private events: { t: number; text: string }[] = [];

  /** `rankPane`: add the weekly momentum-rank pane (daily and weekly views only). */
  constructor(el: HTMLElement, rankPane = true) {
    this.chart = createChart(el, {
      autoSize: true,
      crosshair: { mode: CrosshairMode.Normal },
      rightPriceScale: { borderVisible: false, scaleMargins: { top: 0.08, bottom: 0.22 } },
      timeScale: { borderVisible: false, timeVisible: true, secondsVisible: false, rightOffset: 4 },
    });
    this.candles = this.chart.addSeries(CandlestickSeries, {
      priceLineVisible: true, lastValueVisible: true,
      // No labels at or below 0: the volume pane's margin under the lowest price would otherwise be labelled
      // with prices no stock trades at (0.00, -100.00 under a stock that rose from 30 to 350).
      priceFormat: { type: 'custom', minMove: 0.05, formatter: (p: number) => (p <= 0 ? '' : p.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })) },
    });
    this.volume = this.chart.addSeries(HistogramSeries, { priceScaleId: 'vol', priceFormat: { type: 'volume' }, lastValueVisible: false, priceLineVisible: false });
    this.chart.priceScale('vol').applyOptions({ scaleMargins: { top: 0.84, bottom: 0 } });
    this.markers = createSeriesMarkers(this.candles, []);
    this.patterns = new PatternLayer(this.chart);
    this.planMarks = new PlanMarks(this.candles);
    this.tradeLayer = new TradeLayer(this.chart);
    // A second pane: the weekly momentum rank, 1 at the top.
    this.rank = this.chart.addSeries(LineSeries, { lineWidth: 2, priceLineVisible: false, lastValueVisible: true, title: 'Momentum rank', crosshairMarkerVisible: false, priceFormat: { type: 'custom', minMove: 1, formatter: (p: number) => `#${Math.round(p)}` } }, rankPane ? 1 : 0);
    if (rankPane) {
      this.rank.priceScale().applyOptions({ invertScale: true, scaleMargins: { top: 0.1, bottom: 0.1 } });
      this.chart.panes()[1]?.setHeight(90);
    } else this.rank.applyOptions({ visible: false });
    this.applyTheme();
  }

  applyTheme() {
    const C = palette();
    this.chart.applyOptions({
      layout: { background: { type: ColorType.Solid, color: 'transparent' }, textColor: C.mute, fontFamily: '"JetBrains Mono Variable", ui-monospace, monospace', fontSize: 11, attributionLogo: false },
      grid: { vertLines: { color: C.gridSoft }, horzLines: { color: C.gridSoft } },
      crosshair: { vertLine: { color: C.mute, labelBackgroundColor: C.tagBg }, horzLine: { color: C.mute, labelBackgroundColor: C.tagBg } },
    });
    this.candles.applyOptions({ upColor: C.g, downColor: C.r, borderUpColor: C.g, borderDownColor: C.r, wickUpColor: C.g, wickDownColor: C.r });
    for (const l of this.stopLines) l.applyOptions({ color: C.amber });
    this.rank.applyOptions({ color: C.blue });
    if (this.data.length) this.paintVolume();
    if (this.overlays) this.setOverlays(this.overlays);
    this.planMarks?.repaint();
  }

  setOverlays(o: Overlays) {
    this.overlays = o;
    const C = palette();
    // Lines first: the time axis is shared by every series, so the markers must be placed only once
    // all of them hold the new candles' times (else they land on the old series' positions).
    for (const l of this.stopLines) this.chart.removeSeries(l);
    this.stopLines = [];
    const runs: { time: UTCTimestamp; value: number }[][] = [];
    let cur: { time: UTCTimestamp; value: number }[] | null = null;
    for (const p of o.stop) {
      if (p.v === null) { cur = null; continue; }
      if (!cur) runs.push((cur = []));
      cur.push({ time: p.t as UTCTimestamp, value: p.v });
    }
    runs.forEach((pts, n) => {
      const last = n === runs.length - 1 && o.stop[o.stop.length - 1]?.v !== null; // still open: label today's level
      const l = this.chart.addSeries(LineSeries, { color: C.amber, lineWidth: 2, lineStyle: LineStyle.Dashed, priceLineVisible: false, lastValueVisible: last, title: last ? 'Trailing stop' : '', crosshairMarkerVisible: false });
      l.setData(pts);
      this.stopLines.push(l);
    });
    this.rank.setData(o.rank.map((p) => ({ time: p.t as UTCTimestamp, value: p.v })));
    if (this.rankLine) this.rank.removePriceLine(this.rankLine);
    this.rankLine = o.rank.length ? this.rank.createPriceLine({ price: 20, color: C.mute2, lineStyle: LineStyle.Dotted, lineWidth: 1, axisLabelVisible: true, title: 'top 20' }) : null;
    const patternMarks = [...this.patterns.set(o.patterns), ...this.tradeLayer.set(this.tradeDraw)];
    this.markers.setMarkers([...o.marks.map((m) => m.kind === 'entry'
      ? { time: m.t as UTCTimestamp, position: 'belowBar' as const, shape: 'arrowUp' as const, color: C.g, text: 'New high' }
      : { time: m.t as UTCTimestamp, position: 'aboveBar' as const, shape: 'arrowDown' as const, color: C.amber, text: 'Stop' }), ...patternMarks,
      ...this.events.map((e) => ({ time: e.t as UTCTimestamp, position: 'aboveBar' as const, shape: 'circle' as const, color: C.mute, text: e.text }))]
      .sort((a, b) => (a.time as number) - (b.time as number)));
  }


  /** Show the last n candles (a range button), or everything. */
  showLast(n: number | null) {
    const len = this.data.length;
    if (!len) return;
    if (n === null || n >= len) { this.reset(); return; }
    this.chart.timeScale().setVisibleLogicalRange({ from: len - n, to: len + 3 });
  }

  /** New candles. `fit` shows the whole range (on a timeframe or range change); otherwise the view stays. */
  setData(data: Candle[], intraday: boolean, fit: boolean) {
    this.data = data;
    if (intraday !== this.intraday) { this.intraday = intraday; this.chart.applyOptions({ timeScale: { timeVisible: intraday } }); }
    this.candles.setData(data.map((c) => ({ time: c.t as UTCTimestamp, open: c.o, high: c.h, low: c.l, close: c.c })));
    this.paintVolume();
    if (fit) this.reset();
  }

  private paintVolume() {
    const C = palette();
    this.volume.setData(this.data.map((c) => ({ time: c.t as UTCTimestamp, value: c.v, color: alpha(c.c >= c.o ? C.g : C.r, 0.4) })));
  }

  setScale(s: Scale) {
    this.chart.priceScale('right').applyOptions({ mode: s === 'log' ? PriceScaleMode.Logarithmic : s === 'pct' ? PriceScaleMode.Percentage : PriceScaleMode.Normal });
  }

  /** The candle under the crosshair (or the latest), for the OHLC readout. */
  candleAt(logical: number | null): Candle | undefined {
    if (!this.data.length) return undefined;
    const i = logical == null ? this.data.length - 1 : Math.max(0, Math.min(this.data.length - 1, Math.round(logical)));
    return this.data[i];
  }

  /** Corporate actions (ex-dates) to mark, times snapped to these candles. */
  setEvents(list: { t: number; text: string }[]) { this.events = list; if (this.overlays) this.setOverlays(this.overlays); }
  /** Paper trades to draw (times snapped to these candles with snapTime). */
  setTrades(list: TradeDraw[]) { this.tradeDraw = list; if (this.overlays) this.setOverlays(this.overlays); }
  /** The start of the candle containing time t (null before the first candle). */
  snapTime(t: number): number | null {
    let lo = 0, hi = this.data.length - 1, ans = -1;
    while (lo <= hi) { const mid = (lo + hi) >> 1; if (this.data[mid].t <= t) { ans = mid; lo = mid + 1; } else hi = mid - 1; }
    return ans >= 0 ? this.data[ans].t : null;
  }

  /** The desk's plan levels (an empty list clears them). */
  setMarks(m: PlanMark[]) { this.planMarks.set(m); }

  zoom(factor: number) {
    const ts = this.chart.timeScale(), r = ts.getVisibleLogicalRange();
    if (!r) return;
    const mid = (r.from + r.to) / 2, half = ((r.to - r.from) / 2) * factor;
    ts.setVisibleLogicalRange({ from: mid - half, to: mid + half });
  }

  reset() { this.chart.timeScale().fitContent(); }

  /** The visible window: right edge as the END of its candle's period (so a day maps to its last intraday
   * candle), width in candles, and whether it is at the latest candle. */
  getViewport(): { right: number; bars: number; atEnd: boolean } | null {
    const r = this.chart.timeScale().getVisibleLogicalRange(), n = this.data.length;
    if (!r || !n) return null;
    const i = Math.max(0, Math.min(Math.round(r.to), n - 1));
    const step = n > 1 ? (i + 1 < n ? this.data[i + 1].t - this.data[i].t : this.data[i].t - this.data[i - 1].t) : 86400;
    return { right: this.data[i].t + step - 1, bars: Math.max(10, r.to - r.from), atEnd: r.to >= n - 2 };
  }
  setViewport(vp: { right: number; bars: number; atEnd: boolean }) {
    const n = this.data.length;
    if (!n) return;
    let to = n - 1 + 3;
    if (!vp.atEnd) { let lo = 0, hi = n - 1; to = 0; while (lo <= hi) { const mid = (lo + hi) >> 1; if (this.data[mid].t <= vp.right) { to = mid; lo = mid + 1; } else hi = mid - 1; } }
    this.chart.timeScale().setVisibleLogicalRange({ from: to - vp.bars, to });
  }
  destroy() { this.chart.remove(); }
}
